import React, { useRef, useMemo, useState } from 'react';
import classnames from 'classnames';
import { history, useSelector, useDispatch } from 'umi';
import { useDeepCompareEffect } from 'ahooks';
import { Icon, Layout, Tab } from 'tea-component';
import Loading from '@gp/components/Loading';
import PageAccess from '@gp/components/PageAccess';
import Tabs from '@gp/components/NewIconTabs';
import GeneralImageNew from '@gp/components/GeneralImageNew';
import ALinkCard from '@gp/components/ALinkCard';
import HeaderDetails from '@gp/components/HeaderDetails';
import FixedHeader from '@gp/components/HeaderDetails/FixedHeader';
import DetailsHeader from './components/Header';
import ProjectSwitcher from './components/ProjectSwitcher';
import GameDetail from './GameDetail';
import Milestone from './Milestone';
import ProjectUpdate from './ProjectUpdate';
import Finance from './Finance';
import Stakeholder from './Stakeholder';
import Technical from './Technical';
import useRequest from '@gp/hooks/useRequest';
import useMilestones from './hook/useMilestones';
import useInitialMilestoneId from './hook/useInitialMilestoneId';
import useProjectsRecords from './_hooks/useProjectsRecords';
import useProjectsInfo from './_hooks/useProjectsInfo';
import useProjects from './_hooks/useProjects';
import {
  GAME_DETAIL_TAB_DATA_MODEL_NAMESPACE,
  IGameDetailTabDataState,
} from '@gp/models/gameDetailTabData';
import { queryPageAuthority } from '@gp/services/global';
import {
  getProjectVisualcase,
  getProjectSimilar,
  getProjectInformation,
  getProjectDetailHistorys,
  getGamePillar,
  getElevatorPitch,
} from '@gp/services/projectDetail';
import { fetchProjectMilestones } from '@gp/services/milestone';
import { getLocationIsValid, resetPageScrollTop } from '@gp/utils/common';
import { getKeyMilestones } from './Milestone/utils';
import { SLASH_G_POP } from '@gp/constants/const';
import { DESCRIPTION_B } from '@gp/constants/noPermissionTips';
import { PIPELINE_PROJECT_DETAIL_TABS_NEW_ICON_CFG_MAPPING } from './constants';
import SvgIcon from '@/pages/home/components/SvgIcon';
import type { IRecord, ITab } from '@gp/types/global';
import type { IProjectDTO } from '@gp/types/projectDetail';
import useSearchParams from '@gp/hooks/useSearchParams';
import '@gp/assets/css/pages/projectDetail.css';
import '@gp/assets/css/pages/projectEdit.css';
import './index.scss';

const { Content } = Layout;

const ACCESS_KEY_MAPPING: IRecord<string> = {
  gameDetail: 'project_info',
  milestone: 'project_milestone',
  projectUpdate: 'project_update',
  finance: 'finance',
  kpi: 'kpi',
  stakeholder: 'stakeholder',
  technical: 'technical',
};

const replaceProjectDetailsPageHistory = (filter: IRecord) => {
  const filterStr = encodeURIComponent(JSON.stringify(filter || {}));
  history.replace(
    `${SLASH_G_POP}/Pipeline/ProjectList/Detail?filter=${filterStr}`,
  );
};

const ProjectDetail: React.FC = () => {
  const filterObj: IRecord = getLocationIsValid('/Pipeline/ProjectList');
  const { tab: urlTab, projectId: urlProjectId } = filterObj || {};

  const dispatch = useDispatch();
  const urlParams: any = useSearchParams();

  const {
    data: accessListOriginal,
    loading: accessListLoading,
    run: accessListRun,
  } = useRequest(queryPageAuthority, { manual: true });

  // 详情页每个模块的权限信息
  const {
    data: detailInfoAuthorityData,
    loading: detailInfoAuthorityLoading,
    run: detailInfoAuthorityRun,
  } = useRequest(queryPageAuthority, { manual: true });

  // 单独调用更新信息
  const { run: updateGameInformationRun } = useRequest(getProjectInformation, {
    manual: true,
  });

  const {
    data: gamePillarData,
    loading: gamePillarLoading,
    run: getGamePillarRun,
    refresh: getGamePillarRefresh,
  } = useRequest(getGamePillar, { manual: true });

  const {
    data: elevatorPitchData,
    loading: elevatorPitchLoading,
    run: getElevatorPitchRun,
    refresh: getElevatorPitchRefresh,
  } = useRequest(getElevatorPitch, { manual: true });

  const {
    data: visualcaseDataOriginal,
    loading: visualcaseLoading,
    run: visualcaseRun,
    refresh: visualCaseDataRefresh,
  } = useRequest(getProjectVisualcase, { manual: true });

  const {
    data: milestonesRes,
    loading: milestonesResLoading,
    run: productionMilestoneRun,
    refresh: milestoneDataRefresh,
  } = useRequest(fetchProjectMilestones, { manual: true });

  const {
    data: similarData,
    loading: similarLoading,
    run: similarRun,
    refresh: similarDataRefresh,
  } = useRequest(getProjectSimilar, { manual: true });

  const {
    data: projectDetailHistorysData,
    loading: projectDetailHistorysLoading,
    run: projectDetailHistorysRun,
  } = useRequest(getProjectDetailHistorys, { manual: true });

  const { tab: activeId, defaultActiveId } = useSelector<
  any,
  IGameDetailTabDataState
  >((state) => state?.[GAME_DETAIL_TAB_DATA_MODEL_NAMESPACE] || {});

  const {
    loading: projectsRecordsLoading,
    data: projectsRecords,
    onPushRecord: onPushProjectsRecord,
    projectsRecordsRefresh,
  } = useProjectsRecords();

  const { projectsLoading, projects, projectsRefresh } = useProjects();
  const { milestoneId, clearMilestoneId, clearMilestoneIdByTabId } =
    useInitialMilestoneId();

  const {
    fetchGameInformationRun,
    showOpinion,
    opinionId,
    gameInformationDataValue,
    gameInformationLoading,
    intelligenceId,
    projectId,
    setProjectId,
    getJumpUrl,
    onClickJump,
    entityType,
    fetchGameInforCond,
    milestoneCategory,
    actionItemCategory,
  } = useProjectsInfo({
    urlProjectId,
  });

  const setActiveId = (payload: string) => {
    dispatch({
      type: `${GAME_DETAIL_TAB_DATA_MODEL_NAMESPACE}/updateTab`,
      payload,
    });
  };

  const loading = useMemo<boolean>(
    () =>
      accessListLoading || detailInfoAuthorityLoading || gameInformationLoading,
    [accessListLoading, detailInfoAuthorityLoading, gameInformationLoading],
  );

  const accessList = useMemo<IRecord<boolean>>(
    () => ({
      ...accessListOriginal,
      project_milestone: !!detailInfoAuthorityData?.milestone,
    }),
    [accessListOriginal, detailInfoAuthorityData],
  );

  const gameInformationData = useMemo<IRecord>(() => {
    const gameInformationDataOriginalData =
      gameInformationDataValue?.data || {};
    return {
      ...gameInformationDataOriginalData,
      name: gameInformationDataOriginalData?.project || '',
      business_model: gameInformationDataOriginalData?.business_model || [],
      genre: gameInformationDataOriginalData?.genre || [],
    };
  }, [gameInformationDataValue]);

  const showVisible = useMemo<boolean>(() => {
    if (gameInformationLoading) return true;
    const milestones = gameInformationData?.milestone;
    return !Array.isArray(milestones) || !milestones.length;
  }, [gameInformationLoading, gameInformationData]);

  const milestoneData = milestonesRes?.data || {
    latest_edition: { update_time: '' },
    list: [],
  };

  const { milestones } = useMilestones(milestoneData);

  const { currentMilestone } = useMemo(() => {
    const { currentMilestone } = getKeyMilestones(
      milestoneData?.list,
      gameInformationData?.current_milestone_id,
      gameInformationData?.next_milestone_id,
    );
    return { currentMilestone };
  }, [milestoneData, gameInformationData]);

  const pageContainer = useRef<HTMLElement | null>(null);

  // 用户是否拥有查看权限
  const [userIsAbleSee, setUserIsAbleSee] = useState(false);
  // 用户是否拥有编辑权限
  const [userIsAbleEdit, setUserIsAbleEdit] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  // 新增Milestone弹窗
  const [addMilestoneVisible, setAddMilestoneVisible] = useState(false);
  const [isAdd, setIsAdd] = useState(true);
  // 时间轴展开折叠 false 展开 ，收起 true
  const [timeVisible, setTimeVisible] = useState(false);

  const initialize = (projectId?: string) => {
    if (!projectId) return;
    accessListRun({ assort: 'game_detail', project_id: projectId });
    detailInfoAuthorityRun({
      assort: 'game_detail_info',
      project_id: projectId,
    });
    fetchGameInformationRun(projectId);
    fetchGameInforCond();
  };

  const handleTabChange = (tab?: Tab) => {
    const { id = defaultActiveId } = tab || {};
    // project_id为46时，点击performance页签跳转到经分指定页面
    if (projectId === '46' && id === 'kpi') {
      const url = `${window.location.origin}/v2/dashboard/game/pc_nightingale/overview/daily`;
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.click();
      return;
    }
    if (id === 'gameDetail') {
      const { project_info: gameDetailsAccess } = accessList || {};
      if (gameDetailsAccess) {
        visualcaseRun({ id: projectId });
        updateGameInformationRun({ id: projectId });
      }
    }
    const filter: IRecord = { ...filterObj, tab: id };
    // tab切换时，删除详情页带的milestone
    if (Reflect.has(filter, 'projectUpdateInitMilestone')) {
      Reflect.deleteProperty(filter, 'projectUpdateInitMilestone');
    }
    clearMilestoneIdByTabId(id);
    replaceProjectDetailsPageHistory(filter);
    setActiveId(id);
    resetPageScrollTop(pageContainer.current);
  };

  const handleSelectProjectId = (
    projectIdPayload?: string,
    projectPayload?: IProjectDTO | null,
  ) => {
    if (!projectIdPayload) return;
    if (projectIdPayload === projectId) {
      initialize(projectIdPayload);
      return;
    }
    const filter = {
      ...filterObj,
      projectId: projectIdPayload,
      project: projectPayload?.project || '',
    };
    replaceProjectDetailsPageHistory(filter);
    setProjectId(projectIdPayload);
  };

  useDeepCompareEffect(() => {
    if (gameInformationDataValue) {
      if (gameInformationDataValue?.code === 30010) {
        // 没有查看权限
        setUserIsAbleSee(false);
      } else {
        // 有查看权限
        setUserIsAbleSee(true);
        setActiveId(urlTab || defaultActiveId);
      }
    }
  }, [gameInformationDataValue]);

  useDeepCompareEffect(() => {
    if (accessListLoading || detailInfoAuthorityLoading) return;
    const {
      project_info: gameDetailsAccess,
      project_milestone: milestoneAccess,
      edit_project_info: gameDetailsEditAccess,
    } = accessList || {};
    if (gameDetailsAccess) {
      visualcaseRun({ id: projectId });
      similarRun({ id: projectId });
      getGamePillarRun({ project_id: projectId });
      getElevatorPitchRun({ project_id: projectId });
      projectDetailHistorysRun({ id: projectId });
    }

    if (milestoneAccess) {
      productionMilestoneRun({ id: projectId });
    }

    setUserIsAbleSee(gameDetailsAccess);
    setUserIsAbleEdit(gameDetailsEditAccess);
  }, [accessListLoading, detailInfoAuthorityLoading, accessList]);

  // 初始化开始
  useDeepCompareEffect(() => {
    initialize(projectId);

    return () => {
      setActiveId(defaultActiveId);
    };
  }, [projectId]);

  // 页签配置
  const tabs: ITab[] = [
    {
      id: 'gameDetail',
      label: 'Overview',
      component: (
        <GameDetail
          pageContainer={pageContainer}
          setCurrentProgress={setCurrentProgress}
          filterObj={filterObj}
          detailInfoLoading={gameInformationLoading}
          milestoneData={milestoneData?.list}
          detailInfoAuthorityData={detailInfoAuthorityData}
          tableLoading={milestonesResLoading}
          visualcaseData={visualcaseDataOriginal}
          visualcaseLoading={visualcaseLoading}
          similarData={similarData}
          gamePillarData={gamePillarData}
          elevatorPitchData={elevatorPitchData}
          similarLoading={similarLoading}
          gamePillarLoading={gamePillarLoading}
          elevatorPitchLoading={elevatorPitchLoading}
          gameInformationData={gameInformationData}
          gameInformationLoading={gameInformationLoading}
          detailAccessList={accessList}
          updateOnLoading={projectDetailHistorysLoading}
          updateOnData={projectDetailHistorysData}
          getGamePillarRefresh={getGamePillarRefresh}
          getElevatorPitchRefresh={getElevatorPitchRefresh}
          gameInformationDataRefresh={fetchGameInformationRun}
          visualCaseDataRefresh={visualCaseDataRefresh}
          similarDataRefresh={similarDataRefresh}
          userIsAbleEdit={userIsAbleEdit}
        />
      ),
    },
    {
      id: 'milestone',
      label: 'Milestone',
      component: (
        <Milestone
          pageContainer={pageContainer}
          userIsAbleEdit={userIsAbleEdit}
          editable={!!accessList?.edit_milestone}
          detailInfoAuthorityData={detailInfoAuthorityData}
          projectDetailData={gameInformationData}
          loading={gameInformationLoading || milestonesResLoading}
          updatedDate={milestoneData?.latest_edition}
          milestoneData={milestoneData?.list}
          milestoneId={milestoneId}
          addMilestoneVisible={addMilestoneVisible}
          setAddMilestoneVisible={setAddMilestoneVisible}
          milestones={milestoneData?.list}
          milestoneCategory={milestoneCategory}
          actionItemCategory={actionItemCategory}
          onRefreshProjectDetails={() => {
            clearMilestoneId();
            fetchGameInformationRun();
          }}
          onRefreshMilestones={() => {
            productionMilestoneRun({ id: projectId });
          }}
          isAdd={isAdd}
          setIsAdd={setIsAdd}
        />
      ),
    },
    {
      id: 'projectUpdate',
      label: 'Update',
      component: (
        <ProjectUpdate
          pageContainer={pageContainer}
          userIsAbleSee={userIsAbleSee}
          userIsAbleEdit={accessList?.edit_project_update}
          filterObj={filterObj}
          projectDetailData={gameInformationData}
          milestones={milestones}
          // onUpdateRenewableFrequencySuccess={handleUpdateMilestones}
          detailAccessList={accessList}
          actionItemCategory={actionItemCategory}
          onRefreshProjectDetails={fetchGameInformationRun}
          onRefreshMilestones={() => {
            productionMilestoneRun({ id: projectId });
          }}
        />
      ),
    },
    {
      id: 'finance',
      label: 'Finance',
      component: (
        <Finance
          {...{
            pageContainer,
            projectId,
            projectDetailData: gameInformationData,
            currentMilestone,
          }}
        />
      ),
    },
    {
      id: 'stakeholder',
      label: 'Stakeholder',
      component: (
        <Stakeholder
          pageContainer={pageContainer}
          projectDetailData={gameInformationData}
          detailAccessList={accessList}
          onRefreshProjectDetails={fetchGameInformationRun}
        />
      ),
    },
    {
      id: 'technical',
      label: 'Tech',
      component: (
        <Technical
          editable={!!accessList?.edit_technical}
          projectId={gameInformationData?.project_id}
        />
      ),
    },
  ];

  const titleOperation = useMemo(
    () => (
      <>
        {intelligenceId && (
          <ALinkCard
            url={getJumpUrl(intelligenceId, 'Intelligence', entityType)}
          >
            <span
              className={'dbg-project-details-header-right-span'}
              onClick={() =>
                onClickJump(intelligenceId, 'Intelligence', entityType)
              }
            >
              {'Go to Intelligence'}
              <SvgIcon
                style={{ width: '14px', height: '14px', marginLeft: '4px' }}
                type="agent-right-arrow"
              />
            </span>
          </ALinkCard>
        )}
        {opinionId && (
          <ALinkCard url={getJumpUrl(opinionId, 'Opinions', entityType)}>
            <span
              className={classnames('dbg-project-details-header-right-span', {
                'dbg-disabled-span': !showOpinion,
              })}
              onClick={() => onClickJump(opinionId, 'Opinions', entityType)}
            >
              {'Go to Opinions'}
              <SvgIcon
                style={{ width: '14px', height: '14px', marginLeft: '4px' }}
                type="agent-right-arrow"
              />
            </span>
          </ALinkCard>
        )}
      </>
    ),
    [intelligenceId, opinionId, entityType, showOpinion],
  );

  if (loading) return <Loading loading />;

  return (
    <Content
      ref={pageContainer}
      className={classnames('dbg-game-details dbg-project-details', {
        'dbg-game-details--more-milestone': !showVisible && !timeVisible,
      })}
    >
      <HeaderDetails
        className={classnames('games-bg dbg-project-details-header', {
          'games-bg-height': !showVisible && !timeVisible,
        })}
        title="Game Details"
        tabList={tabs}
        activeId={activeId}
        newMarkCfgMapping={PIPELINE_PROJECT_DETAIL_TABS_NEW_ICON_CFG_MAPPING}
        goBackHandle={() => {
          if (urlParams?.hasOwnProperty('kw')) {
            history.push(`/g-pop/Pipeline/ProjectList`);
          } else {
            history.goBack();
          }
        }}
        change={handleTabChange}
        operation={titleOperation}
      >
        <DetailsHeader
          projectsLoading={projectsLoading}
          projects={projects}
          projectsRecordsLoading={projectsRecordsLoading}
          projectsRecords={projectsRecords}
          onPushProjectsRecord={onPushProjectsRecord}
          showAddMilestoneBtn={!!accessList?.edit_milestone}
          showVisible={showVisible}
          timeVisible={timeVisible}
          activeId={activeId}
          setActiveId={setActiveId}
          userIsAbleEdit={userIsAbleEdit}
          currentProjectId={projectId}
          onSelectCurrentProjectId={handleSelectProjectId}
          detailInfo={gameInformationData}
          gameInformationData={gameInformationData}
          milestoneData={{
            list: gameInformationData?.milestone || [],
          }}
          currentProgress={currentProgress}
          gameInformationDataRefresh={fetchGameInformationRun}
          milestoneDataRefresh={milestoneDataRefresh}
          projectsRecordsRefresh={projectsRecordsRefresh}
          projectsRefresh={projectsRefresh}
          setAddMilestoneVisible={setAddMilestoneVisible}
          setIsAdd={setIsAdd}
          milestoneCategory={milestoneCategory}
          getTimeVisible={() => {
            setTimeVisible(!timeVisible);
          }}
        />
      </HeaderDetails>
      <Content.Body full>
        {/* 头部吸顶结构 */}
        <FixedHeader>
          <div className="dbg-fixedheader-infobox">
            <Icon
              className="dbg-fixedheader-infobox-back"
              type="btnback"
              onClick={() => {
                if (urlParams?.hasOwnProperty('kw')) {
                  history.push(`/g-pop/Pipeline/ProjectList`);
                } else {
                  history.goBack();
                }
              }}
            />
            <GeneralImageNew
              className="dbg-fixedheader-infobox-img"
              domain="game"
              width={24}
              height={24}
              src={gameInformationData?.cover}
            />
            <ProjectSwitcher
              projectsLoading={projectsLoading}
              projects={projects}
              projectsRecordsLoading={projectsRecordsLoading}
              projectsRecords={projectsRecords}
              onPushProjectsRecord={onPushProjectsRecord}
              name={gameInformationData?.project}
              value={projectId}
              onChange={handleSelectProjectId}
            />
          </div>

          <div className="dbg-fixedheader-tabs">
            <Tabs
              ceiling
              animated={false}
              newMarkCfgMapping={
                PIPELINE_PROJECT_DETAIL_TABS_NEW_ICON_CFG_MAPPING
              }
              tabs={tabs}
              activeId={activeId}
              onActive={handleTabChange}
            />
          </div>
        </FixedHeader>
        {loading && (
          <div
            style={{ height: 'calc(100vh - 187px - 60px)', color: '#262832' }}
          >
            <Loading />
          </div>
        )}
        {!loading &&
          tabs.map((tab) => {
            const { id, component } = tab || {};
            if (id !== activeId) return null;
            return (
              <PageAccess
                className="dbg-pipeline-project-detail-page-access"
                key={id}
                isAccess={!!accessList?.[ACCESS_KEY_MAPPING[id] || id]}
                description={DESCRIPTION_B}
              >
                {component}
              </PageAccess>
            );
          })}
      </Content.Body>
    </Content>
  );
};

export default ProjectDetail;
